import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Badge,
  Flex,
  Grid,
  GridItem,
  Card,
  CardBody,
  Stack,
  Divider,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  useColorModeValue,
  ButtonGroup
} from '@chakra-ui/react';
import { 
  FaCalendarAlt, 
  FaUsers, 
  FaBuilding,
} from 'react-icons/fa';
import { UserInfoContext } from '../../provider/UserInfoProvider';
import { UseConstant } from '../../libraries/UseConstant';
import LinkButton from '../../components/custom/LinkButton';
import AddButton from '../../components/custom/AddButton';
import { useParams } from 'react-router-dom';
import { calculateMonthsDuration, convertToYYYYMMDD } from '../../components/utils/dateTimeUtil';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';

export default function ProjectDetailsPage() {
  const { constant } = UseConstant();
  const { userInfo } = useContext(UserInfoContext);
  const isAdmin = userInfo?.user_type === constant.USER_TYPE_ADMIN;
  const id = useParams().id;

  // 取得データ
  const [project, setProject] = useState({});

  const { get } = useApi();
  const { showError } = useToast();

  // 検索処理
  const getProject = async () => {
      try {
          const url = isAdmin ? `/project/${id}` : `/my-project/${id}`;
          const response = await get(url);

          if (response.isSuccess === false) {
              showError('データの取得に失敗しました');
              return;
          }

          setProject(response.data || {});
      } catch (error) {
          showError('データの取得に失敗しました');
      }
  };

  useEffect(() => {
      getProject();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <>
      <div className='admin-page-header'>
        <Flex justify={"flex-end"}>
          <ButtonGroup gap={2}>
            {isAdmin 
              ? <LinkButton buttonText={'戻る'} buttonUrl={'/projects'} />
              : 
              (
                <>
                  <AddButton buttonText={'新規登録'} buttonUrl={'/me/add-project'} />
                  <LinkButton buttonText={'ダッシュボード'} buttonUrl={'/user-dashboard'} />
                </>
              )
            }
          </ButtonGroup>
        </Flex>
      </div>
      <Box minH="100vh">
        <Container maxW="">

          {/* Title Card */}
          <Card bg={cardBg} mb={6} boxShadow="lg">
            <CardBody>
              <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
                <Box flex="1">
                  <Heading size="xl" mb={3}>
                    {project.project_title}
                  </Heading>
                  <HStack spacing={3} flexWrap="wrap">
                    <Badge colorScheme={project.is_going_on === "1" ? "green" : "red"} fontSize="md" px={3} py={1}>
                      {project.is_going_on === "1" ? "進行中" : "完了"}
                    </Badge>
                    <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                      {project.type === "1" ? "社内" : "社外"}
                    </Badge>
                  </HStack>
                </Box>
              </Flex>
            </CardBody>
          </Card>

          {/* Main Grid Layout */}
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} mb={6}>
            {/* Project Overview */}
            <GridItem>
              <Card bg={cardBg} h="100%" boxShadow="lg">
                <CardBody>
                  <Heading size="md" mb={4} color="orange.500">
                    プロジェクト概要
                  </Heading>
                  <Stack spacing={4}>
                    <Flex align="center">
                      <Icon as={FaCalendarAlt} mr={3} color="purple.500" boxSize={5} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.500">期間</Text>
                        <Text fontSize="lg">
                          {convertToYYYYMMDD(project.start_date)} 〜 {project.is_on_going === "1" ? "現在" : convertToYYYYMMDD(project.end_date)}
                        </Text>
                        <Text fontSize="sm" color="gray.600" fontWeight={"bold"}>
                          ({calculateMonthsDuration(project.start_date, project.is_on_going === "1" ? null : project.end_date)}ヶ月)
                        </Text>
                      </Box>
                    </Flex>
                    <Divider />
                    <Flex align="center">
                      <Icon as={FaBuilding} mr={3} color="blue.500" boxSize={5} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.500">部署</Text>
                        <Text fontSize="lg">{project.department?.department}</Text>
                      </Box>
                    </Flex>

                    <Divider />

                    <Flex align="center">
                      <Icon as={FaUsers} mr={3} color="green.500" boxSize={5} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.500">チーム人数</Text>
                        <Text fontSize="lg">{project.no_of_people}名</Text>
                      </Box>
                    </Flex>

                  </Stack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Project Details */}
            <GridItem>
              <Card bg={cardBg} h="100%" boxShadow="lg">
                <CardBody>
                  <Heading size="md" mb={4} color="orange.500">
                    プロジェクト詳細
                  </Heading>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold" mb={2} color="blue.600">
                        【概要】
                      </Text>
                      <Text whiteSpace="pre-line" lineHeight="tall">
                        {project.details}
                      </Text>
                    </Box>

                    {/* 役割 */}
                    <Box>
                      <Text fontWeight="bold" mb={2} color="blue.600">
                        【役割】
                      </Text>
                      <Text whiteSpace="pre-line" lineHeight="tall">
                        {Array.isArray(project.role_users) && project.role_users.length > 0 ? project.role_users.map((role_user, index) => (
                          <Box key={index} mb={3}>
                            <Text mb={1}>{role_user.role?.role}</Text>
                          </Box>
                        )) : '情報なし'}
                      </Text>
                    </Box>

                    {/* 担当規模 */}
                    <Box>
                      <Text fontWeight="bold" mb={2} color="blue.600">
                        【担当規模】
                      </Text>
                      <Text whiteSpace="pre-line" lineHeight="tall">
                        {Array.isArray(project.responsibility_users) && project.responsibility_users.length > 0 
                          ? project.responsibility_users
                              .map((responsibility_user) => responsibility_user.responsibility?.responsibility)
                              .filter(Boolean)
                              .join(', ')
                          : '情報なし'
                        }
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          {/* Technology Stack Table */}
          <Card bg={cardBg} boxShadow="lg">
            <CardBody>
              <Heading size="md" mb={4} color="orange.500">
                使用技術
              </Heading>
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead bg="orange.50">
                    <Tr>
                      <Th fontSize="md" py={4}>カテゴリ</Th>
                      <Th fontSize="md">技術</Th>
                      <Th fontSize="md">期間(ヶ月)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {project.project_technologies?.map((projectTech, index) => {
                      const currentStack = projectTech.technology?.stack;
                      const previousStack = index > 0 ? project.project_technologies[index - 1]?.technology?.stack : null;
                      const showStack = currentStack !== previousStack;

                      return (
                        <Tr 
                          key={projectTech.id}
                          _hover={{ bg: hoverBg }}
                          borderBottom="1px"
                          borderColor={borderColor}
                        >
                          <Td fontWeight="semibold">
                            {showStack && currentStack && (
                              <HStack>
                                <Text>{currentStack}</Text>
                              </HStack>
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                              {projectTech.technology?.technology || '情報なし'}
                            </Badge>
                          </Td>
                          <Td>
                            {calculateMonthsDuration(
                              projectTech.technology_users[0].start_date, 
                              projectTech.technology_users[0].is_going_on === "1" ? null : projectTech.technology_users[0].end_date
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </>
  );
}